import {
  bustAllSessionCache,
  bustUpdatedResources,
} from "../utils/bust-loader-cache";
import { dropCopiableCyclesFromSession } from "../utils/copiable-cycle";
import { dropUnsafeEditExercises } from "../utils/rill-loader-cache";

let started = !process.browser;

// Middleware to bust cache, by default this will delete the cache on every request.
// To cache the data of a loader, "no-bust" should be added inside the loader's meta array option.
// The other strings should match the name of the collections where the data is fetched.
// Do if the endpoint returns data from the cycle and exercise collections the meta array should be ["no-bust", "cycle", "exercise"].
// When a document is modified the last-modified collection should also be modified
// the last modified collection will have 1 document per user (the id matches the id of the user) where each field is the name of the collection and the date it was last modified
// it's important that the last modified colelction is kept up to date so if there is an update we compare the dates and bust the cache to fetch new data from the api
// also important to pass a ttl option when using no-bust so the cache expires anyways after a certain period of time passes to ensure accurate data
const CacheBuster = (ctx, next) => {
  const { session } = ctx;
  // Drop lists that must not be in the session before this request is saved:
  // a copiable-cycle cache from an older build, and an unscoped or oversized
  // editExercises cache (program overview used to store every exercise).
  dropCopiableCyclesFromSession(session);
  dropUnsafeEditExercises(session);
  return Promise.resolve(bustCache(ctx, next)).then(
    (value) => {
      // A loader may have just cached a new oversized list. Locals already
      // hold it for this render; don't persist it.
      dropUnsafeEditExercises(session);
      return value;
    },
    (error) => {
      dropUnsafeEditExercises(session);
      throw error;
    }
  );
};

const bustCache = ({ api, session, locals, req }, next) => {
  if (!started) {
    started = true;
    return next();
  }
  if (req.body.omitModified || req.query.omitModified) return next();
  if (
    req.pathname === "/actions/refresh" ||
    req.pathname === "/actions/refresh/"
  )
    return next();

  let bustAll = true;

  if (session.items) {
    for (const item of session.items) {
      const { meta = [] } = item;
      if (meta.includes("no-bust")) {
        // there's an item in the session that shouldn't be busted all the time
        bustAll = false;
        break;
      }
    }
  }

  if (bustAll) {
    // no need to keep anything in cache, bust everything
    bustAllSessionCache(session);
    return next();
  } else {
    if (!locals.user) return next();
    return api("modified", { query: { user: locals.user._id } })
      .then((res) => res.json())
      .then((modified) => {
        const lastModified = session.get("_last_modified");
        session.set("_last_modified", modified);
        if (!lastModified || !modified) {
          // no modified document or session found, can't determine what should be cached so bust everything
          bustAllSessionCache(session);
          return;
        }
        // Once /modified is present, unpinned entries are cleared (the old
        // per-resource loop did this even when that collection's timestamp
        // had not moved). no-bust entries are cleared only for collections
        // whose timestamp is newer.
        let sawResource = false;
        const updatedResources = [];
        for (const resource in modified) {
          if (Object.hasOwn(modified, resource)) {
            sawResource = true;
            const isNewResource = modified[resource] && !lastModified[resource];
            const resourceUpdated =
              isNewResource ||
              new Date(modified[resource]) > new Date(lastModified[resource]);
            if (resourceUpdated) updatedResources.push(resource);
          }
        }
        if (sawResource) {
          bustUpdatedResources(session, updatedResources, {
            dropUnpinned: true,
          });
        }
      })
      .then(next);
  }
};

export default CacheBuster;
